import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import fileDownload from "js-file-download";

import {
  grayColor,
  whiteColor,
  warningColor,
  dangerColor,
  blendColors,
} from "../../../../assets/jss/material-dashboard-pro-react";
import useThemeColors from "../../../../hooks/useThemeColors";
import {
  downloadBlockLogFileV3,
  fetchBlockLogMeasureSeriesBatchV3,
} from "../../../../services/inputsV3Service";

const MONTHS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"];

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    fontFamily: '"Roboto", "Inter", "Segoe UI", Arial, sans-serif',
    color: grayColor[2],
    fontSize: 12,
    fontWeight: 300,
  },
  hierarchyBlock: {
    position: "relative",
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 10,
    background: whiteColor,
    overflow: "hidden",
    boxShadow: "0 4px 14px rgba(20, 30, 45, 0.035)",
  },
  hierarchyTint: {
    background: (props) =>
      `linear-gradient(90deg, ${blendColors(
        props.clientColor || dangerColor[2],
        whiteColor,
        0.035,
      )}, ${whiteColor} 22%)`,
  },
  hierarchyWarning: {
    borderLeft: `4px solid ${warningColor[0]}`,
  },
  hierarchyLoss: {
    borderLeft: `4px solid ${dangerColor[0]}`,
  },
  hierarchyHead: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "11px 13px",
    borderBottom: `1px solid ${grayColor[11]}`,
    background: "#fbfcfd",
  },
  hierarchyBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    borderRadius: "50%",
    background: (props) =>
      blendColors(props.clientColor || dangerColor[2], whiteColor, 0.13),
    color: (props) => props.clientColor || dangerColor[2],
    fontSize: 11,
    fontWeight: 700,
    flex: "0 0 auto",
  },
  hierarchyTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: grayColor[2],
  },
  hierarchySubtitle: {
    marginLeft: "auto",
    color: grayColor[1],
    fontSize: 10.5,
  },
  hierarchyBody: {
    padding: "10px 13px 12px",
  },
  nodeList: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    gap: 4,
    "&:before": {
      content: '""',
      position: "absolute",
      left: 10,
      top: 8,
      bottom: 8,
      width: 1,
      background: grayColor[10],
      zIndex: 2,
    },
  },
  node: {
    position: "relative",
    display: "grid",
    gridTemplateColumns: "var(--log-node-indent, 0px) 22px minmax(0, 1fr) auto",
    columnGap: 0,
    alignItems: "start",
    minHeight: 22,
    padding: "1px 0",
    borderRadius: 5,
    transition: "background-color 100ms ease",
    "&:after": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: 5,
      zIndex: 0,
      pointerEvents: "none",
      transition: "background-color 100ms ease",
    },
    "&:hover:after": {
      backgroundColor: "#f7f8fa",
    },
  },
  nodeGroup: {
    marginTop: 12,
    "&:first-child": {
      marginTop: 0,
    },
  },
  nodeSection: {
    marginTop: 9,
    "& $nodeTitle": {
      color: "#243140",
      fontWeight: 500,
    },
  },
  nodeNested: {
    minHeight: 20,
    marginTop: -4,
    padding: 0,
    "& $nodeTitle": {
      lineHeight: "18px",
    },
    "& $nodeDot": {
      width: 10,
      height: 10,
      marginTop: 4,
    },
    "&:before": {
      content: '""',
      position: "absolute",
      left: 10,
      top: 10,
      width: "calc(var(--log-node-indent, 0px) + 1px)",
      height: 1,
      background: grayColor[10],
    },
  },
  nodeTable: {
    minHeight: "auto",
    marginBottom: 10,
    marginLeft: "-20px",
    padding: 0,
    "&:before": {
      display: "none",
    },
    "& $nodeDot": {
      display: "none",
    },
    "& $nodeContent": {
      paddingLeft: 0,
    },
    "& $nodeTitle": {
      minHeight: "auto",
      lineHeight: 1,
    },
  },
  nodeDot: {
    gridColumn: 2,
    width: 12,
    height: 12,
    borderRadius: "50%",
    border: (props) => `1px solid ${props.clientColor || dangerColor[2]}`,
    background: (props) =>
      blendColors(props.clientColor || dangerColor[2], whiteColor, 0.1),
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: grayColor[1],
    fontSize: 0,
    position: "relative",
    zIndex: 3,
    marginTop: 4,
    justifySelf: "center",
  },
  nodeContent: {
    gridColumn: 3,
    minWidth: 0,
    paddingLeft: 8,
    position: "relative",
    zIndex: 2,
  },
  nodeDotGroup: {
    borderColor: (props) => props.clientColor || dangerColor[2],
    background: (props) =>
      blendColors(props.clientColor || dangerColor[2], whiteColor, 0.1),
  },
  nodeDotMetric: {
    borderColor: (props) => props.clientColor || dangerColor[2],
    color: (props) => props.clientColor || dangerColor[2],
    background: (props) =>
      blendColors(props.clientColor || dangerColor[2], whiteColor, 0.1),
  },
  nodeDotInput: {
    borderColor: (props) => props.clientColor || dangerColor[2],
    background: (props) =>
      blendColors(props.clientColor || dangerColor[2], whiteColor, 0.1),
  },
  nodeDotRationale: {
    borderColor: (props) => props.clientColor || dangerColor[2],
    background: (props) =>
      blendColors(props.clientColor || dangerColor[2], whiteColor, 0.1),
  },
  nodeDotDownload: {
    borderColor: grayColor[6],
    color: grayColor[1],
    background: blendColors(grayColor[6], whiteColor, 0.1),
  },
  nodeDotWarning: {
    borderColor: dangerColor[0],
    color: dangerColor[0],
    background: blendColors(dangerColor[0], whiteColor, 0.16),
  },
  nodeDotLoss: {
    borderColor: dangerColor[0],
    color: dangerColor[0],
    background: blendColors(dangerColor[0], whiteColor, 0.16),
  },
  nodeTitle: {
    display: "block",
    minHeight: 18,
    lineHeight: "18px",
    color: grayColor[2],
  },
  nodeValue: {
    color: "#243140",
    fontWeight: 600,
  },
  richNumber: {
    color: "#243140",
    fontWeight: 600,
    lineHeight: "inherit",
  },
  measureInline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    whiteSpace: "nowrap",
    verticalAlign: "baseline",
  },
  measureValue: {
    color: "#243140",
    fontWeight: 600,
    lineHeight: "inherit",
  },
  measureCompareBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 15,
    height: 15,
    border: `1px solid ${grayColor[10]}`,
    borderRadius: "50%",
    background: "#fbfcfd",
    color: grayColor[2],
    padding: 0,
    fontSize: 9,
    lineHeight: "13px",
    cursor: "pointer",
    transition: "background 120ms, border-color 120ms, color 120ms",
    "&:hover": {
      borderColor: (props) => props.clientColor || dangerColor[2],
      color: (props) => props.clientColor || dangerColor[2],
      background: whiteColor,
    },
    "&:disabled": {
      opacity: 0.7,
      cursor: "default",
    },
  },
  measureCompareBtnOpen: {
    borderColor: (props) => props.clientColor || dangerColor[2],
    color: (props) => props.clientColor || dangerColor[2],
  },
  monoBlock: {
    margin: 0,
    color: grayColor[2],
    fontFamily: '"Consolas", "Courier New", monospace',
    fontSize: 11.2,
    fontWeight: 400,
    lineHeight: 1.38,
    whiteSpace: "pre",
    overflowX: "auto",
    maxWidth: "100%",
    padding: "2px 0",
  },
  logTableWrap: {
    maxWidth: "100%",
    overflowX: "auto",
    display: "inline-block",
    margin: "1px 0 2px",
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 6,
    background: "#fbfcfd",
  },
  logTableCaption: {
    padding: "5px 8px 0",
    color: grayColor[1],
    fontSize: 11.2,
    fontWeight: 500,
  },
  logTable: {
    borderCollapse: "separate",
    borderSpacing: 0,
    minWidth: 360,
    color: grayColor[2],
    fontSize: 11.3,
    lineHeight: 1.2,
    "& th": {
      padding: "4px 8px 3px",
      color: grayColor[1],
      fontWeight: 700,
      borderBottom: `1px solid ${grayColor[10]}`,
      whiteSpace: "nowrap",
    },
    "& td": {
      padding: "3px 8px",
      whiteSpace: "nowrap",
      borderBottom: `1px solid ${grayColor[12]}`,
    },
    "& tbody tr:last-child td": {
      borderBottom: "none",
    },
    "& th:not(:first-child), & td:not(:first-child)": {
      paddingLeft: 18,
    },
  },
  logTableRight: {
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
    fontFamily: '"Consolas", "Courier New", monospace',
  },
  logTableLeft: {
    textAlign: "left",
  },
  nodeDetail: {
    display: "block",
    marginTop: 2,
    color: grayColor[1],
    fontSize: 11,
    lineHeight: 1.35,
  },
  inputToken: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    border: `1px solid ${grayColor[10]}`,
    borderRadius: 999,
    background: "#f7f8fa",
    color: "#3f4b59",
    padding: "2px 7px",
    marginBottom: 1,
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 120ms, border-color 120ms, box-shadow 120ms",
    "&:hover": {
      background: whiteColor,
      borderColor: "#b8c0ca",
      boxShadow: "0 0 0 2px rgba(67, 78, 92, 0.06)",
    },
  },
  refLine: {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    minWidth: 0,
  },
  refLineText: {
    flex: "0 0 auto",
    color: grayColor[2],
    fontWeight: 600,
  },
  rationaleLine: {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    minWidth: 0,
  },
  rationaleLabel: {
    flex: "0 0 auto",
    color: grayColor[2],
    fontWeight: 700,
  },
  rationaleText: {
    minWidth: 0,
    color: grayColor[2],
    fontWeight: 300,
  },
  refTokenWrap: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "4px 5px",
    minWidth: 0,
  },
  downloadToken: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    border: `1px solid ${grayColor[10]}`,
    borderRadius: 999,
    background: "#f7f8fa",
    color: "#3f4b59",
    padding: "2px 7px",
    marginBottom: 1,
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
    verticalAlign: "baseline",
    transition: "background 120ms, border-color 120ms, box-shadow 120ms",
    "&:before": {
      content: '"↓"',
      color: grayColor[1],
      fontWeight: 700,
    },
    "&:hover": {
      background: whiteColor,
      borderColor: "#b8c0ca",
      boxShadow: "0 0 0 2px rgba(67, 78, 92, 0.06)",
    },
  },
  downloadDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
  },
  compareBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gridColumn: 4,
    justifySelf: "end",
    marginLeft: 8,
    border: `1px solid ${grayColor[10]}`,
    borderRadius: 999,
    background: "#fbfcfd",
    color: grayColor[2],
    height: 18,
    padding: "0 7px",
    fontSize: 10.5,
    fontWeight: 600,
    lineHeight: "16px",
    cursor: "pointer",
    transition: "background 120ms, border-color 120ms, color 120ms",
    whiteSpace: "nowrap",
    "&:hover": {
      borderColor: (props) => props.clientColor || dangerColor[2],
      color: (props) => props.clientColor || dangerColor[2],
      background: whiteColor,
    },
    "&:disabled": {
      opacity: 0.75,
      cursor: "default",
    },
  },
  compareBtnOpen: {
    borderColor: (props) => props.clientColor || dangerColor[2],
    color: (props) => props.clientColor || dangerColor[2],
  },
  compareBtnLoading: {
    minWidth: 74,
    justifyContent: "center",
    cursor: "default",
  },
  compare: {
    margin: "6px 0 10px calc(var(--log-node-indent, 0px) + 30px)",
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 8,
    overflow: "hidden",
    background: grayColor[11],
  },
  compareHead: {
    display: "flex",
    alignItems: "center",
    gap: 7,
    padding: "7px 10px",
    background: whiteColor,
    borderBottom: `1px solid ${grayColor[11]}`,
    fontSize: 11,
    color: grayColor[1],
    "& strong": {
      color: grayColor[2],
      fontWeight: 600,
    },
  },
  compareUnit: {
    marginLeft: "auto",
    border: `1px solid ${grayColor[11]}`,
    borderRadius: 999,
    background: "#fbfcfd",
    color: grayColor[1],
    padding: "2px 7px",
    whiteSpace: "nowrap",
  },
  compareGrid: {
    display: "grid",
    gridTemplateColumns: "52px repeat(12, minmax(0, 1fr))",
    gap: 1,
    background: grayColor[11],
    width: "100%",
  },
  gridCell: {
    minWidth: 0,
    minHeight: 38,
    background: whiteColor,
    padding: "5px 6px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    justifyContent: "center",
    fontVariantNumeric: "tabular-nums",
  },
  gridCellValid: {
    cursor: "pointer",
    transition: "background 120ms, box-shadow 120ms, transform 120ms",
    "&:hover": {
      background: "#fff8f7",
      boxShadow: "inset 0 0 0 1px #efb5b1",
    },
  },
  gridCellHeader: {
    background: "#f7f8fa",
    color: grayColor[1],
    fontSize: 10,
    fontWeight: 600,
  },
  gridCellYear: {
    background: "#f7f8fa",
    color: grayColor[1],
    fontSize: 10,
    fontWeight: 600,
    alignItems: "flex-end",
    paddingRight: 10,
  },
  gridCellOut: {
    color: grayColor[6],
    background: "#f7f8fa",
    alignItems: "center",
  },
  gridCellCurrent: {
    background: (props) =>
      blendColors(props.clientColor || dangerColor[2], whiteColor, 0.13),
    boxShadow: (props) =>
      `inset 0 0 0 1.5px ${props.clientColor || dangerColor[2]}`,
    position: "relative",
    zIndex: 1,
  },
  gridCellRef: {
    background: "#eef2f6",
  },
  gridCellPinned: {
    background: "#fff5f4",
    boxShadow: (props) =>
      `inset 0 0 0 1.5px ${props.clientColor || dangerColor[2]}`,
  },
  cellValue: {
    fontSize: 11.5,
    fontWeight: 500,
    lineHeight: 1.15,
    color: grayColor[2],
  },
  cellDelta: {
    height: 11,
    lineHeight: "11px",
    marginTop: 2,
    fontSize: 9.5,
    display: "inline-flex",
    alignItems: "center",
    gap: 2,
  },
  deltaPos: { color: "#2e7d32" },
  deltaNeg: { color: dangerColor[0] },
  deltaNeutral: { color: grayColor[1] },
  deltaRef: {
    color: (props) => props.clientColor || dangerColor[2],
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  deltaCurrent: {
    color: grayColor[1],
    fontStyle: "italic",
  },
  empty: {
    border: `1px dashed ${grayColor[10]}`,
    borderRadius: 8,
    padding: 18,
    textAlign: "center",
    color: grayColor[1],
    background: "#f7f8fa",
  },
}));

function chooseScale(values, unit) {
  if (unit !== "$") return { divisor: 1, suffix: unit || "" };
  const finite = values.map(Number).filter((v) => Number.isFinite(v));
  if (!finite.length) return { divisor: 1, suffix: "$" };
  const max = Math.max(...finite.map((v) => Math.abs(v)));
  if (max >= 1000000000) return { divisor: 1000000000, suffix: "$ B" };
  if (max >= 1000000) return { divisor: 1000000, suffix: "$ M" };
  if (max >= 1000) return { divisor: 1000, suffix: "$ k" };
  return { divisor: 1, suffix: "$" };
}

function decimalsFor(value) {
  const abs = Math.abs(Number(value));
  if (!Number.isFinite(abs)) return 0;
  if (abs >= 100) return 0;
  if (abs >= 10) return 1;
  if (abs >= 1) return 2;
  return 3;
}

function formatValue(value, scale = { divisor: 1 }) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  const scaled = n / (scale.divisor || 1);
  const decimals = decimalsFor(scaled);
  return scaled.toLocaleString("es-CL", {
    minimumFractionDigits: decimals === 0 ? 0 : undefined,
    maximumFractionDigits: decimals,
  });
}

function formatMeasureDisplay(measure) {
  if (measure?.display) return String(measure.display);
  const n = Number(measure?.value);
  if (!Number.isFinite(n)) return "—";
  const precision =
    measure?.precision !== null && measure?.precision !== undefined
      ? Number(measure.precision)
      : Math.abs(n) >= 100
        ? 0
        : 1;
  const formatted = n.toLocaleString("es-CL", {
    minimumFractionDigits: precision === 0 ? 0 : undefined,
    maximumFractionDigits: Number.isFinite(precision) ? precision : 1,
  });
  if (measure?.unit === "$") return `$ ${formatted}`;
  if (measure?.unit === "%") return `${formatted}%`;
  if (measure?.unit) return `${formatted} ${measure.unit}`;
  return formatted;
}

function unitLabel(unit, scale) {
  if (scale?.suffix) return scale.suffix;
  if (unit === "%") return "%";
  return unit || "valor";
}

function periodKey(year, month) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function inRange(year, month, firstYear, firstMonth, lastYear, lastMonth) {
  const key = year * 100 + month;
  const min = (firstYear || year - 1) * 100 + (firstMonth || 1);
  const max = (lastYear || year + 1) * 100 + (lastMonth || 12);
  return key >= min && key <= max;
}

function toneForChapter(chapter, index) {
  if (chapter.tone) return chapter.tone;
  const text =
    `${chapter.title || ""} ${chapter.description || ""}`.toLowerCase();
  if (
    text.includes("no asignado") ||
    text.includes("perda") ||
    text.includes("loss")
  ) {
    return "loss";
  }
  if (
    text.includes("gap") ||
    text.includes("warning") ||
    text.includes("sin ")
  ) {
    return "warning";
  }
  return index === 0 ? "tint" : "";
}

function nodeTone(node) {
  const text = `${node.kind || ""} ${node.text || ""}`.toLowerCase();
  if (node.kind === "download" || node.downloads?.length) return "download";
  if (node.kind === "table") return "group";
  if (node.kind === "section") return "group";
  const measureTones = (Array.isArray(node?.measures) ? node.measures : [])
    .map((m) => m?.tone)
    .filter(Boolean);
  if (node?.tone === "loss" || measureTones.includes("loss")) return "loss";
  if (node?.tone === "warning" || measureTones.includes("warning"))
    return "warning";
  if (text.includes("no asignado") || text.includes("sin cruce")) return "loss";
  if (text.includes("sin ") || text.includes("gap")) return "warning";
  if (node.kind === "summary" && !node.indicator) return "group";
  if (isInputNode(node)) return "input";
  if (isRationaleNode(node)) return "rationale";
  if (node.indicator) return "metric";
  return "";
}

function dotLabel() {
  return "";
}

function displayText(node) {
  const text = node?.text || "";
  if (!node?.input_refs?.length) return text;
  return text.replace(/\s*\[[^\]]+\]/g, "").trim();
}

function displayLabel(text) {
  return String(text || "").replace(/^inputs:/i, "Inputs:");
}

function rationaleText(node) {
  return String(node?.text || "").replace(/^(racional|raz[oó]n):\s*/i, "");
}

function rationaleLabel(node) {
  return /^raz[oó]n:/i.test(String(node?.text || "")) ? "Razón:" : "Racional:";
}

function isInputNode(node) {
  return node?.kind === "input" || !!node?.input_refs?.length;
}

function isRationaleNode(node) {
  const text = String(node?.text || "").toLowerCase();
  return node?.kind === "rationale" || text.startsWith("racional:");
}

function humanizeMetaName(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function inputLabel(ref, inputLabelByMeta) {
  const metaName = ref?.input_name || "";
  const payloadLabel = ref?.label;
  if (payloadLabel && payloadLabel !== metaName) return payloadLabel;
  return inputLabelByMeta?.[metaName] || humanizeMetaName(metaName) || metaName;
}

function normalizeMeasure(measure) {
  if (!measure?.key) return null;
  return {
    ...measure,
    display: formatMeasureDisplay(measure),
  };
}

function cellText(cell) {
  if (cell && typeof cell === "object" && !Array.isArray(cell)) {
    return String(cell.text ?? cell.measure?.display ?? "");
  }
  return String(cell ?? "");
}

function cellMeasure(cell) {
  return cell && typeof cell === "object" && !Array.isArray(cell)
    ? normalizeMeasure(cell.measure)
    : null;
}

function collectNodeMeasures(node) {
  const measures = [];
  if (Array.isArray(node?.measures)) {
    node.measures.forEach((measure) => {
      const normalized = normalizeMeasure(measure);
      if (normalized) measures.push(normalized);
    });
  } else if (node?.indicator) {
    const normalized = normalizeMeasure(node.indicator);
    if (normalized) measures.push(normalized);
  }
  (node?.table?.rows || []).forEach((row) => {
    (row || []).forEach((cell) => {
      const normalized = cellMeasure(cell);
      if (normalized) measures.push(normalized);
    });
  });
  const seen = new Set();
  return measures.filter((measure) => {
    if (seen.has(measure.key)) return false;
    seen.add(measure.key);
    return true;
  });
}

function renderNodeMainText(node, classes, renderMeasureValue) {
  if (node?.kind === "table" && node?.table) {
    return renderLogTable(node, classes, renderMeasureValue);
  }
  if (isMonoNode(node)) {
    return <pre className={classes.monoBlock}>{displayText(node)}</pre>;
  }
  if (isRationaleNode(node)) {
    return (
      <span className={classes.rationaleLine}>
        <strong className={classes.rationaleLabel}>
          {rationaleLabel(node)}
        </strong>
        <span className={classes.rationaleText}>
          {renderRichText(
            rationaleText(node),
            classes,
            collectNodeMeasures(node),
            renderMeasureValue,
          )}
        </span>
      </span>
    );
  }
  return renderRichText(
    displayLabel(displayText(node)),
    classes,
    collectNodeMeasures(node),
    renderMeasureValue,
  );
}

function renderLogTable(node, classes, renderMeasureValue) {
  const headers = Array.isArray(node?.table?.headers) ? node.table.headers : [];
  const rows = Array.isArray(node?.table?.rows) ? node.table.rows : [];
  const alignments = Array.isArray(node?.table?.alignments)
    ? node.table.alignments
    : [];
  const title = node?.table?.title || "";
  if (!headers.length) {
    return <pre className={classes.monoBlock}>{displayText(node)}</pre>;
  }
  const alignClass = (idx) =>
    alignments[idx] === "r" ? classes.logTableRight : classes.logTableLeft;

  return (
    <div className={classes.logTableWrap}>
      {title ? <div className={classes.logTableCaption}>{title}</div> : null}
      <table className={classes.logTable}>
        <thead>
          <tr>
            {headers.map((header, idx) => (
              <th key={`${header}-${idx}`} className={alignClass(idx)}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={`row-${rowIdx}`}>
              {headers.map((_, colIdx) => (
                <td
                  key={`cell-${rowIdx}-${colIdx}`}
                  className={alignClass(colIdx)}
                >
                  {cellMeasure(row?.[colIdx]) && renderMeasureValue
                    ? renderMeasureValue(cellMeasure(row?.[colIdx]))
                    : cellText(row?.[colIdx])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function isMonoNode(node) {
  const text = String(node?.text || "");
  return (
    node?.kind === "table" ||
    text.includes("\n") ||
    text.includes("─") ||
    text.includes("│") ||
    text.includes("┼")
  );
}

function renderRichText(
  text,
  classes,
  measures = [],
  renderMeasureValue = null,
) {
  const normalizedMeasures = (measures || [])
    .map(normalizeMeasure)
    .filter(Boolean)
    .filter((measure) => measure.display)
    .sort((a, b) => b.display.length - a.display.length);
  if (normalizedMeasures.length && renderMeasureValue) {
    const source = String(text || "");
    const out = [];
    let idx = 0;
    let partIdx = 0;
    while (idx < source.length) {
      const match = normalizedMeasures.find((measure) =>
        source.startsWith(measure.display, idx),
      );
      if (match) {
        out.push(renderMeasureValue(match, `measure-${idx}-${match.key}`));
        idx += match.display.length;
        continue;
      }
      const nextIndexes = normalizedMeasures
        .map((measure) => source.indexOf(measure.display, idx + 1))
        .filter((pos) => pos >= 0);
      const nextIdx = nextIndexes.length
        ? Math.min(...nextIndexes)
        : source.length;
      const chunk = source.slice(idx, nextIdx);
      out.push(
        ...renderRichText(chunk, classes, [], null).map((part) => (
          <React.Fragment key={`chunk-${partIdx++}`}>{part}</React.Fragment>
        )),
      );
      idx = nextIdx;
    }
    return out;
  }
  const pattern =
    /(\$ ?-?\d[\d.,]*(?:\s?(?:M|MM|k|B))?(?:\/[A-Za-zÀ-ÿ%]+)?|-?\d[\d.,]* ?%(?:\/[A-Za-zÀ-ÿ%]+)?|-?\d[\d.,]*(?:\s?(?:M|MM|k|B|HL|SKU|SKUs|clientes|líneas|lineas|posiciones|reglas|horas))?(?:\/[A-Za-zÀ-ÿ%]+)?)/g;
  const numberPattern =
    /^(\$ ?-?\d[\d.,]*(?:\s?(?:M|MM|k|B))?(?:\/[A-Za-zÀ-ÿ%]+)?|-?\d[\d.,]* ?%(?:\/[A-Za-zÀ-ÿ%]+)?|-?\d[\d.,]*(?:\s?(?:M|MM|k|B|HL|SKU|SKUs|clientes|líneas|lineas|posiciones|reglas|horas))?(?:\/[A-Za-zÀ-ÿ%]+)?)$/;
  const parts = String(text || "").split(pattern);
  return parts.map((part, idx) =>
    numberPattern.test(part) ? (
      <strong key={`${part}-${idx}`} className={classes.richNumber}>
        {part}
      </strong>
    ) : (
      part
    ),
  );
}

function nodeDepth(level) {
  const parsed = Number(level);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.min(2, Math.floor(parsed));
}

function nodeIndent(level) {
  return nodeDepth(level) * 24;
}

function isSectionNode(node, nextNode) {
  if (node?.kind === "section") return true;
  return (
    node?.kind === "summary" &&
    !node.indicator &&
    !node.input_refs?.length &&
    !node.downloads?.length &&
    !collectNodeMeasures(node).length &&
    nextNode?.kind === "table"
  );
}

function visualNodeLevel(node, nextNode, prevNode) {
  if (isSectionNode(node, nextNode)) {
    return Math.max(0, Number(node?.level || 0) - 1);
  }
  if (node?.kind === "table" && isSectionNode(prevNode, node)) {
    const sectionLevel = Math.max(0, Number(prevNode?.level || 0) - 1);
    return sectionLevel + 1;
  }
  return node?.level;
}

function nodeLevelClass(classes, level) {
  return nodeDepth(level) > 0 ? classes.nodeNested : "";
}

function nodeGroupClass(classes, node) {
  return nodeTone(node) === "group" && nodeDepth(node?.level) === 0
    ? classes.nodeGroup
    : "";
}

function nodeTableClass(classes, node) {
  return node?.kind === "table" ? classes.nodeTable : "";
}

function nodeSectionClass(classes, node, nextNode) {
  return isSectionNode(node, nextNode) ? classes.nodeSection : "";
}

function dotClass(classes, node) {
  const tone = nodeTone(node);
  if (tone === "loss") return classes.nodeDotLoss;
  if (tone === "warning") return classes.nodeDotWarning;
  if (tone === "group") return classes.nodeDotGroup;
  if (tone === "metric") return classes.nodeDotMetric;
  if (tone === "input") return classes.nodeDotInput;
  if (tone === "rationale") return classes.nodeDotRationale;
  if (tone === "download") return classes.nodeDotDownload;
  return "";
}

function deltaClass(classes, delta) {
  if (delta > 0.5) return classes.deltaPos;
  if (delta < -0.5) return classes.deltaNeg;
  return classes.deltaNeutral;
}

const BlockLogViewer = ({
  logData,
  currentYear,
  currentMonth,
  firstYear,
  firstMonth,
  lastYear,
  lastMonth,
  onOpenInput,
  inputLabelByMeta,
}) => {
  const { clientColor } = useThemeColors();
  const classes = useStyles({ clientColor });
  const [openIndicator, setOpenIndicator] = useState(null);
  const [seriesByKey, setSeriesByKey] = useState({});
  const [loadingKey, setLoadingKey] = useState(null);
  const [pinnedByKey, setPinnedByKey] = useState({});
  const [previewByKey, setPreviewByKey] = useState({});

  const chapters = Array.isArray(logData?.chapters) ? logData.chapters : [];
  const logId = logData?.id;
  const blockMetaName =
    logData?.meta?.block_meta_name || logData?.block_meta_name;

  const years = useMemo(() => {
    const base = currentYear || logData?.year || new Date().getFullYear();
    return [base - 1, base, base + 1];
  }, [currentYear, logData?.year]);

  const fetchSeriesForMeasures = async (measures) => {
    const keys = (measures || [])
      .map((measure) => measure?.key)
      .filter(Boolean);
    const missing = keys.filter((key) => !seriesByKey[key]);
    if (!missing.length || !blockMetaName) return;
    const payload = await fetchBlockLogMeasureSeriesBatchV3({
      metaName: blockMetaName,
      keys: missing,
    });
    setSeriesByKey((prev) => {
      const next = { ...prev };
      Object.entries(payload?.series || {}).forEach(([key, series]) => {
        next[key] = series;
      });
      missing.forEach((key) => {
        if (!next[key]) next[key] = { values: [] };
      });
      return next;
    });
  };

  const toggleCompare = async (measure) => {
    const key = measure?.key;
    if (!key || !blockMetaName) return;
    setOpenIndicator((prev) => (prev === key ? null : key));
    if (seriesByKey[key]) return;
    setLoadingKey(key);
    try {
      await fetchSeriesForMeasures([measure]);
    } catch {
      setSeriesByKey((prev) => ({ ...prev, [key]: { values: [] } }));
    } finally {
      setLoadingKey(null);
    }
  };

  const handleDownload = async (item) => {
    if (!logId || !item?.filename || !item?.uploaded) return;
    const { blob, filename } = await downloadBlockLogFileV3({
      logId,
      filename: item.filename,
    });
    fileDownload(blob, filename);
  };

  const renderMeasureValue = (measure, key = measure?.key) => {
    const normalized = normalizeMeasure(measure);
    if (!normalized) return null;
    const isLoading = loadingKey === normalized.key;
    const isOpen = openIndicator === normalized.key;
    return (
      <span key={key} className={classes.measureInline}>
        <strong className={classes.measureValue}>{normalized.display}</strong>
        <button
          type="button"
          className={`${classes.measureCompareBtn} ${
            isOpen ? classes.measureCompareBtnOpen : ""
          }`}
          onClick={() => toggleCompare(normalized)}
          disabled={isLoading}
          title="Comparar entre meses"
        >
          {isLoading ? "…" : "⇄"}
        </button>
      </span>
    );
  };

  const renderDelta = (
    indicator,
    item,
    reference,
    isReference,
    isPinnedRef,
  ) => {
    if (loadingKey === indicator.key) return "...";
    if (!reference) {
      return item?.year === currentYear && item?.month === currentMonth
        ? "atual"
        : "";
    }
    if (isReference) return isPinnedRef ? "fixo" : "ref";
    const refValue = Number(reference.value);
    const value = Number(item.value);
    if (
      !Number.isFinite(refValue) ||
      refValue === 0 ||
      !Number.isFinite(value)
    ) {
      return "";
    }
    const delta = ((value - refValue) / refValue) * 100;
    const sign = delta > 0 ? "+" : "";
    return `${sign}${delta.toFixed(1)}%`;
  };

  const renderCompare = (indicator, node) => {
    if (openIndicator !== indicator.key) return null;
    const series = seriesByKey[indicator.key];
    if (!series) return null;
    const values = new Map(
      (series?.values || []).map((v) => [periodKey(v.year, v.month), v]),
    );
    const scale = chooseScale(
      (series?.values || [])
        .filter((v) =>
          inRange(v.year, v.month, firstYear, firstMonth, lastYear, lastMonth),
        )
        .map((v) => v.value),
      indicator.unit,
    );
    const pinned = pinnedByKey[indicator.key] || null;
    const preview = previewByKey[indicator.key] || null;
    const reference = preview || pinned;
    const referenceKey = reference
      ? periodKey(reference.year, reference.month)
      : "";
    const visibleYears = years.filter((year) =>
      MONTHS.some((m) => {
        const month = Number(m);
        const item = values.get(periodKey(year, month));
        return (
          inRange(year, month, firstYear, firstMonth, lastYear, lastMonth) &&
          item &&
          item.value !== null &&
          item.value !== undefined
        );
      }),
    );

    return (
      <div
        className={classes.compare}
        style={{
          "--log-node-indent": `${nodeIndent(node?.level)}px`,
        }}
        onMouseLeave={() =>
          setPreviewByKey((prev) => ({ ...prev, [indicator.key]: null }))
        }
      >
        <div className={classes.compareHead}>
          <span>⇄</span>
          <span>
            Comparar <strong>{indicator.label}</strong> · passe para prévia,
            clique para fixar referência
          </span>
          <span className={classes.compareUnit}>
            unidade: {unitLabel(indicator.unit, scale)}
          </span>
        </div>
        <div className={classes.compareGrid}>
          <div className={`${classes.gridCell} ${classes.gridCellHeader}`} />
          {MONTHS.map((m) => (
            <div
              key={`mh-${m}`}
              className={`${classes.gridCell} ${classes.gridCellHeader}`}
            >
              {m}
            </div>
          ))}
          {visibleYears.map((year) => (
            <React.Fragment key={year}>
              <div className={`${classes.gridCell} ${classes.gridCellYear}`}>
                {year}
              </div>
              {MONTHS.map((m) => {
                const month = Number(m);
                const key = periodKey(year, month);
                const item = values.get(key);
                const validRange = inRange(
                  year,
                  month,
                  firstYear,
                  firstMonth,
                  lastYear,
                  lastMonth,
                );
                const hasValue =
                  item && item.value !== null && item.value !== undefined;
                const valid = validRange && hasValue;
                const isCurrent =
                  year === currentYear && month === currentMonth;
                const isReference = referenceKey === key;
                const isPinnedRef =
                  pinned && periodKey(pinned.year, pinned.month) === key;
                const delta =
                  reference && valid
                    ? ((Number(item.value) - Number(reference.value)) /
                        Number(reference.value)) *
                      100
                    : 0;
                return (
                  <div
                    key={key}
                    className={[
                      classes.gridCell,
                      valid ? classes.gridCellValid : classes.gridCellOut,
                      isCurrent ? classes.gridCellCurrent : "",
                      isReference ? classes.gridCellRef : "",
                      isPinnedRef ? classes.gridCellPinned : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onMouseEnter={() => {
                      if (valid) {
                        setPreviewByKey((prev) => ({
                          ...prev,
                          [indicator.key]: item,
                        }));
                      }
                    }}
                    onClick={() => {
                      if (valid) {
                        setPinnedByKey((prev) => ({
                          ...prev,
                          [indicator.key]: item,
                        }));
                      }
                    }}
                  >
                    {valid ? (
                      <>
                        <span className={classes.cellValue}>
                          {formatValue(item.value, scale)}
                        </span>
                        <span
                          className={[
                            classes.cellDelta,
                            isReference ? classes.deltaRef : "",
                            !reference && isCurrent ? classes.deltaCurrent : "",
                            reference && !isReference
                              ? deltaClass(classes, delta)
                              : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          {renderDelta(
                            indicator,
                            item,
                            reference,
                            isReference,
                            isPinnedRef,
                          )}
                        </span>
                      </>
                    ) : (
                      <span>—</span>
                    )}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  if (!chapters.length) {
    return (
      <div className={classes.empty}>Sem log estruturado para este bloco.</div>
    );
  }

  return (
    <div className={classes.root}>
      {chapters.map((chapter, idx) => {
        const tone = toneForChapter(chapter, idx);
        return (
          <section
            key={chapter.id || idx}
            className={[
              classes.hierarchyBlock,
              tone === "tint" ? classes.hierarchyTint : "",
              tone === "warning" ? classes.hierarchyWarning : "",
              tone === "loss" ? classes.hierarchyLoss : "",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <div className={classes.hierarchyHead}>
              <span className={classes.hierarchyBadge}>{idx + 1}</span>
              <span className={classes.hierarchyTitle}>{chapter.title}</span>
              {chapter.description && (
                <span className={classes.hierarchySubtitle}>
                  {chapter.description}
                </span>
              )}
            </div>
            <div className={classes.hierarchyBody}>
              <div className={classes.nodeList}>
                {(chapter.nodes || []).map((node, nodeIdx) => {
                  const prevNode = chapter.nodes?.[nodeIdx - 1];
                  const nextNode = chapter.nodes?.[nodeIdx + 1];
                  const visualLevel = visualNodeLevel(node, nextNode, prevNode);
                  const indicator = node.indicator;
                  const measures = collectNodeMeasures(node);
                  const activeMeasure = measures.find(
                    (measure) => measure.key === openIndicator,
                  );
                  return (
                    <React.Fragment key={`${chapter.id || idx}-${nodeIdx}`}>
                      <div
                        className={[
                          classes.node,
                          nodeLevelClass(classes, visualLevel),
                          nodeGroupClass(classes, node),
                          nodeSectionClass(classes, node, nextNode),
                          nodeTableClass(classes, node),
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        style={{
                          "--log-node-indent": `${nodeIndent(visualLevel)}px`,
                        }}
                      >
                        <span
                          className={`${classes.nodeDot} ${dotClass(
                            classes,
                            node,
                          )}`}
                        >
                          {dotLabel()}
                        </span>
                        <div className={classes.nodeContent}>
                          <div className={classes.nodeTitle}>
                            {node.kind === "download" &&
                            !!node.downloads?.length ? (
                              <span className={classes.refLine}>
                                <span className={classes.refLineText}>
                                  Download:
                                </span>
                                <span className={classes.refTokenWrap}>
                                  {node.downloads.map((item) => (
                                    <button
                                      key={item.filename || item.label}
                                      type="button"
                                      className={`${classes.downloadToken} ${
                                        !item.uploaded
                                          ? classes.downloadDisabled
                                          : ""
                                      }`}
                                      onClick={() => handleDownload(item)}
                                      disabled={!item.uploaded}
                                    >
                                      {item.label || item.filename}
                                    </button>
                                  ))}
                                </span>
                              </span>
                            ) : node.input_refs?.length ? (
                              <span className={classes.refLine}>
                                <span className={classes.refLineText}>
                                  {renderNodeMainText(
                                    node,
                                    classes,
                                    renderMeasureValue,
                                  )}
                                </span>
                                <span className={classes.refTokenWrap}>
                                  {node.input_refs.map((ref) => (
                                    <button
                                      key={`${ref.input_name}-${ref.year}-${ref.month}`}
                                      type="button"
                                      className={classes.inputToken}
                                      onClick={() => onOpenInput?.(ref)}
                                      title="Abrir input no período"
                                    >
                                      {inputLabel(ref, inputLabelByMeta)}
                                    </button>
                                  ))}
                                </span>
                              </span>
                            ) : (
                              renderNodeMainText(
                                node,
                                classes,
                                renderMeasureValue,
                              )
                            )}
                            {!!indicator && (
                              <span className={classes.nodeValue} />
                            )}
                          </div>
                          {node.kind !== "download" &&
                            !!node.downloads?.length && (
                              <span className={classes.refLine}>
                                <span className={classes.refLineText}>
                                  Download:
                                </span>
                                <span className={classes.refTokenWrap}>
                                  {node.downloads.map((item) => (
                                    <button
                                      key={item.filename || item.label}
                                      type="button"
                                      className={`${classes.downloadToken} ${
                                        !item.uploaded
                                          ? classes.downloadDisabled
                                          : ""
                                      }`}
                                      onClick={() => handleDownload(item)}
                                      disabled={!item.uploaded}
                                    >
                                      {item.label || item.filename}
                                    </button>
                                  ))}
                                </span>
                              </span>
                            )}
                        </div>
                      </div>
                      {activeMeasure && renderCompare(activeMeasure, node)}
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

BlockLogViewer.propTypes = {
  logData: PropTypes.object,
  currentYear: PropTypes.number,
  currentMonth: PropTypes.number,
  firstYear: PropTypes.number,
  firstMonth: PropTypes.number,
  lastYear: PropTypes.number,
  lastMonth: PropTypes.number,
  onOpenInput: PropTypes.func,
  inputLabelByMeta: PropTypes.object,
};

export default BlockLogViewer;
